import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { ScreenLockService } from 'app/core/services/screen-lock.service';
import { ChangePasswordDialogComponent } from 'app/modules/auth/change-password/change-password-dialog.component';
import { EditCompanyProfileComponent } from 'app/modules/settings/edit-company-profile/edit-company-profile.component';
import { MODULE_PERMISSION_KEYS, PermissionService } from 'app/core/permission/permission.service';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { ADMINISTRATION_PERMISSIONS } from 'app/modules/admin/administration-permissions';
import { PhoneDirectoryDialogComponent } from 'app/modules/admin/phone-directory/phone-directory-dialog.component';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'user',
    templateUrl: './user.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'user',
    imports: [
        MatButtonModule,
        MatMenuModule,
        MatIconModule,
        NgClass,
        MatDividerModule,
    ],
    styles: [`
        :host {
            display: inline-flex;
            flex: 0 0 auto;
            align-items: center;
            justify-content: center;
        }

        .user-header-trigger.mat-mdc-icon-button {
            display: inline-flex !important;
            align-items: center;
            justify-content: center;
            width: 40px !important;
            height: 40px !important;
            min-height: 40px !important;
            max-height: 40px !important;
            overflow: visible;
        }

        .user-header-avatar {
            position: relative;
            display: block;
            flex: 0 0 auto;
            width: 32px;
            height: 32px;
        }

        .user-header-avatar-clip {
            display: block;
            width: 32px;
            height: 32px;
            overflow: hidden;
            border-radius: 50%;
            background: var(--fuse-bg-card, #fff);
            box-shadow: 0 0 0 2px #d1d5db, 0 1px 2px rgba(0, 0, 0, 0.06);
        }

        .dark .user-header-avatar-clip {
            box-shadow: 0 0 0 2px #6b7280, 0 0 0 1px rgba(255, 255, 255, 0.1);
        }

        .user-header-trigger.mat-mdc-icon-button img.user-header-avatar-image {
            display: block;
            width: 32px !important;
            height: 32px !important;
            max-width: 32px;
            max-height: 32px;
            aspect-ratio: 1 / 1;
            object-fit: cover;
            object-position: center;
            border-radius: 50%;
        }

        .user-header-trigger.mat-mdc-icon-button img.user-header-avatar-image--contain {
            object-fit: contain;
            padding: 4px;
            box-sizing: border-box;
            background: var(--fuse-bg-card, #fff);
        }

        .user-header-avatar-fallback {
            width: 32px !important;
            height: 32px !important;
        }

        .user-header-avatar-status {
            position: absolute;
            right: 0;
            bottom: 0;
            width: 8px;
            height: 8px;
            border-radius: 50%;
            pointer-events: none;
        }

        .user-header-avatar-status--icon {
            margin: 0 1px 1px 0;
        }
    `],
})
export class UserComponent implements OnInit, OnDestroy {
    @Input() showAvatar: boolean = true;
    @Input() embedded: boolean = false;
    user: any;
    canOpenCompanySettings = false;
    canViewActivityLogs = false;
    canViewPhoneDirectory = false;
    canManageSessions = false;
    canOpenMyHome = false;
    canAccessSupport = false;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _authService: AuthService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _dialog: MatDialog,
        private _router: Router,
        private _userService: UserService,
        private _permissionService: PermissionService,
        private _screenLock: ScreenLockService
    ) {}

    /** Profile picture when set; otherwise company logo. */
    get avatarUrl(): string | null {
        const profilePicture = this.user?.profilePicture || this.user?.avatar;
        if (profilePicture) {
            return profilePicture;
        }

        const company =
            this._authService.profile()?.company ??
            this._authService.profile()?.Company;
        const logo = company?.logo ?? company?.logoUrl ?? company?.Logo;
        return logo ? String(logo) : null;
    }

    get isCompanyLogoFallback(): boolean {
        return (
            !(this.user?.profilePicture || this.user?.avatar) && !!this.avatarUrl
        );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to user changes
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this.user = user;
                this.canOpenCompanySettings = this._permissionService.hasUserPermission(
                    [
                        ADMINISTRATION_PERMISSIONS.manageCompanySettings,
                        ADMINISTRATION_PERMISSIONS.managePostedDocumentEditSettings,
                    ],
                    'any'
                );
                this.canViewActivityLogs = this._permissionService.hasUserPermission(
                    ADMINISTRATION_PERMISSIONS.viewActivityLogs
                );
                this.canViewPhoneDirectory = this._permissionService.hasUserPermission(
                    ADMINISTRATION_PERMISSIONS.viewPhoneDirectory
                );
                this.canManageSessions = this._permissionService.isSuperAdmin();
                this.canOpenMyHome = this._permissionService.isSuperAdmin();
                this.canAccessSupport = this._permissionService.hasPermission(
                    MODULE_PERMISSION_KEYS.support
                );
                this._changeDetectorRef.markForCheck();
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Open company profile in a dialog from any page.
     */
    openEditCompanyProfile(): void {
        this._dialog.open(EditCompanyProfileComponent, {
            width: '720px',
            maxWidth: '95vw',
            maxHeight: '95vh',
            autoFocus: false,
            disableClose: true,
            panelClass: 'company-profile-dialog-panel',
        });
    }

    openChangePassword(): void {
        this._dialog.open(ChangePasswordDialogComponent, {
            width: '480px',
            maxWidth: '95vw',
            autoFocus: false,
            disableClose: true,
            panelClass: 'bp-documents-dialog-panel',
        });
    }

    lockScreen(): void {
        this._screenLock.lock();
    }

    openSettings(): void {
        this._router.navigate(['/company-settings']);
    }

    openActivityLogs(): void {
        this._router.navigate(['/activity-logs']);
    }

    openSessionManagement(): void {
        this._router.navigate(['/session-management']);
    }

    openMyHome(): void {
        this._router.navigate(['/my-home']);
    }

    openPhoneDirectory(): void {
        this._dialog.open(PhoneDirectoryDialogComponent, {
            width: '1100px',
            maxWidth: '96vw',
            maxHeight: '92vh',
            autoFocus: 'input',
            panelClass: 'phone-directory-dialog-panel',
        });
    }

    openSupport(): void {
        const link =
            this._permissionService.findFirstPermittedLinkInModule('support') ??
            '/support/tickets';
        this._router.navigateByUrl(link);
    }

    /**
     * Sign out
     */
    signOut(): void {
        this._authService
            .signOut()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this._router.navigate(['/sign-in']);
            });
    }
}
